// Fields on a customRoute that fall back to the main trackAndTrace config when unset.
// NOTE: 'exceptions' is deliberately excluded - it doesn't follow this "null means inherit"
// pattern. A matched route's exceptions list is authoritative even when empty; main's
// exceptions only apply when no custom route matched at all. See getResolvedRouteExtras.
export const ROUTE_FALLBACK_FIELDS = [
  'customPropertyKey0', 'customPropertyLabel0',
  'customPropertyKey1', 'customPropertyLabel1',
  'customPropertyKey2', 'customPropertyLabel2',
  'extraDuration',
  'logoFileName',
  'bannerFileName',
  'bannerText',
  'orderNotFoundText',
  'secretKeyLabel',
  'orderNumberKey',
  'informationLink',
  'informationText'
];

// Fields copied when creating a new custom route from "main" or from another route.
// Identity fields (name, linkedTo) are deliberately excluded - those always get a fresh value.
export const ROUTE_SETTINGS_FIELDS = [
  'columns',
  'extraDuration',
  'customPropertyKey0', 'customPropertyLabel0',
  'customPropertyKey1', 'customPropertyLabel1',
  'customPropertyKey2', 'customPropertyLabel2',
  'exceptions',
  'logoFileName',
  'bannerFileName',
  'bannerText',
  'orderNotFoundText',
  'secretKeyLabel',
  'orderNumberKey',
  'informationLink',
  'informationText'
];

// Finds the first custom route whose linkedTo criteria matches any job in the order.
export function matchRouteForOrder(orderStatus, customRoutes) {
  if (!customRoutes || !customRoutes.length) {
    return undefined;
  }
  const flattenedJobs = _.flattenDeep(orderStatus);
  return customRoutes.find(route => {
    const key = route.linkedTo.key;
    return route.linkedTo['values'].some(linkedRoute => {
      return flattenedJobs.some(job => {
        const jobValue = job[key];
        if (Array.isArray(jobValue)) {
          return jobValue.includes(linkedRoute.value);
        } else if (jobValue !== null && jobValue !== undefined) {
          return jobValue == linkedRoute.value; // loose equality for flexibility
        }
      });
    });
  });
}

function isUnset(value) {
  return value === null || value === undefined;
}

// Resolves a matched custom route's overridable fields, falling back to the main config
// for any field the route doesn't set. Pass fieldNames to resolve a subset (e.g. just the
// exceptions/extraDuration used at runtime) - defaults to every fallback field.
export function resolveRouteFields(mainConfig, matchedRoute, fieldNames = ROUTE_FALLBACK_FIELDS) {
  const resolved = {};
  fieldNames.forEach(field => {
    const routeValue = matchedRoute ? matchedRoute[field] : undefined;
    resolved[field] = isUnset(routeValue) ? mainConfig[field] : routeValue;
  });
  return resolved;
}

function blankColumns() {
  return {
    activitiesColumn0: {activities: [], label: null},
    activitiesColumn1: {activities: [], label: null},
    activitiesColumn2: {activities: [], label: null},
    activitiesColumn3: {activities: [], label: null}
  };
}

// Builds the settings-fields object for a brand new custom route, from a source object
// (either the main config or an existing route) or blank (source = null).
export function buildRouteSettingsFrom(source) {
  const settings = {};
  ROUTE_SETTINGS_FIELDS.forEach(field => {
    if (field === 'columns') {
      return; // handled below - main and route store their columns in different shapes
    }
    settings[field] = source && !isUnset(source[field]) ? _.cloneDeep(source[field]) : null;
  });

  if (!source) {
    // Blank route: no columns yet, no explicit overrides - everything else falls back to main
    // until the user edits it (extraDuration/exceptions/etc. stay null/unset above).
    settings.columns = blankColumns();
    settings.exceptions = [];
  } else if (source.columns) {
    // Source is an existing custom route - already in the nested {activitiesColumn0-3} shape.
    settings.columns = _.cloneDeep(source.columns);
  } else {
    // Source is the main config - its columns live as flat activitiesColumn0-3 fields.
    settings.columns = {
      activitiesColumn0: source.activitiesColumn0 ? _.cloneDeep(source.activitiesColumn0) : {activities: [], label: null},
      activitiesColumn1: source.activitiesColumn1 ? _.cloneDeep(source.activitiesColumn1) : {activities: [], label: null},
      activitiesColumn2: source.activitiesColumn2 ? _.cloneDeep(source.activitiesColumn2) : {activities: [], label: null},
      activitiesColumn3: source.activitiesColumn3 ? _.cloneDeep(source.activitiesColumn3) : {activities: [], label: null}
    };
  }
  return settings;
}
